// TipTap custom node for TikZ pictures rendered via the backend compilation server.
// Stores raw LaTeX source; sends it to /api/compile-tikz which runs latex + dvisvgm
// and returns an SVG that is displayed inline in the editor.

import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useState, useRef, useCallback, useEffect } from "react";

// ── SVG cache ────────────────────────────────────────────────────────────────
const svgCache = new Map<string, string>();

/**
 * Walk the ProseMirror document and collect all `\def` / `\newcommand` /
 * `\renewcommand` definitions.  These can appear:
 *  - Prepended to other tikzBlock node sources (placed there by the parser)
 *  - In text nodes if the user typed them explicitly
 * The returned string is suitable for insertion before the tikz source.
 */
function collectDocMacroDefs(editor: any): string {
    if (!editor?.state?.doc) return "";
    const defs = new Set<string>();
    const defRe = /\\def\\[a-zA-Z]+(?:#\d)*\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g;
    const newcmdRe = /\\(?:re)?newcommand\{?\\[a-zA-Z]+\}?(?:\[\d+\])?\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g;

    editor.state.doc.descendants((node: any) => {
        let text: string | undefined;
        if (node.type.name === "tikzBlock") {
            text = node.attrs?.source;
        } else if (node.isText) {
            text = node.text;
        }
        if (!text) return;
        for (const m of text.matchAll(defRe)) defs.add(m[0]);
        for (const m of text.matchAll(newcmdRe)) defs.add(m[0]);
    });

    return defs.size > 0 ? [...defs].join("\n") : "";
}

// ── Compile queue (limits concurrency + retries on 429) ─────────────────────
const CONCURRENCY = 3;
let inflight = 0;
let pausedUntil = 0;
const pendingQueue: Array<{ run: () => void }> = [];

function drain() {
    const now = Date.now();
    if (now < pausedUntil) {
        setTimeout(drain, pausedUntil - now + 100);
        return;
    }
    while (inflight < CONCURRENCY && pendingQueue.length > 0) {
        inflight++;
        pendingQueue.shift()!.run();
    }
}

/** Raw fetch to the compile endpoint. */
async function fetchTikzSvg(source: string, preamble?: string): Promise<string> {
    const apiBase =
        ((import.meta as any).env?.VITE_COMPILE_API_URL as string | undefined) ??
        "/api";

    const body: Record<string, string> = { source };
    if (preamble) body.preamble = preamble;

    const response = await fetch(`${apiBase}/compile-tikz`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        let msg = "TikZ compilation failed";
        try {
            const json = (await response.json()) as { error?: string; log?: string };
            msg = json.error ?? msg;
        } catch { /* ignore */ }
        const err = new Error(msg) as Error & { status?: number };
        err.status = response.status;
        throw err;
    }

    return await response.text();
}

/**
 * Compile TikZ source to SVG via the backend server.
 * Requests are queued with limited concurrency.
 * 429 (rate-limited) responses are automatically retried after a delay.
 */
function renderTikzToSvg(source: string, preamble?: string): Promise<string> {
    const cacheKey = (preamble ?? "") + "||" + source;
    const cached = svgCache.get(cacheKey);
    if (cached) return Promise.resolve(cached);

    return new Promise<string>((resolve, reject) => {
        const attempt = () => {
            fetchTikzSvg(source, preamble)
                .then((svg) => {
                    svgCache.set(cacheKey, svg);
                    inflight--;
                    resolve(svg);
                    drain();
                })
                .catch((err: Error & { status?: number }) => {
                    inflight--;
                    if (err.status === 429) {
                        // Rate-limited: pause queue, re-enqueue at front, retry later
                        pausedUntil = Date.now() + 6_000;
                        pendingQueue.unshift({ run: attempt });
                        drain();
                    } else {
                        reject(err);
                        drain();
                    }
                });
        };
        pendingQueue.push({ run: attempt });
        drain();
    });
}

// ── TikZ NodeView (React) ────────────────────────────────────────────────────

function TikzNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
    const source: string = node.attrs.source ?? "";
    const caption: string = node.attrs.caption ?? "";
    const label: string = node.attrs.label ?? "";

    const [editing, setEditing] = useState(!source);
    const [renderState, setRenderState] = useState<"idle" | "loading" | "done" | "error">("idle");
    const [errorMsg, setErrorMsg] = useState("");
    const [svgHtml, setSvgHtml] = useState<string>("");
    const [editingCaption, setEditingCaption] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const captionRef = useRef<HTMLInputElement>(null);
    const [editValue, setEditValue] = useState(source);

    // Render TikZ when source changes and not editing
    useEffect(() => {
        if (editing || !source) return;
        let cancelled = false;

        setRenderState("loading");
        setErrorMsg("");

        const preamble = collectDocMacroDefs(editor);
        renderTikzToSvg(source, preamble)
            .then((html) => {
                if (!cancelled) {
                    setSvgHtml(html);
                    setRenderState("done");
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setRenderState("error");
                    setErrorMsg(err?.message ?? "Render failed");
                }
            });

        return () => { cancelled = true; };
    }, [source, editing]);

    // Auto-focus textarea when entering edit mode
    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [editing]);

    const handleSave = useCallback(() => {
        const trimmed = editValue.trim();
        if (trimmed) {
            updateAttributes({ source: trimmed });
        }
        setEditing(false);
    }, [editValue, updateAttributes]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setEditValue(source);
            setEditing(false);
        }
    }, [handleSave, source]);

    const handleDelete = useCallback(() => {
        if (editor && typeof getPos === "function") {
            const pos = getPos();
            if (typeof pos === "number") {
                editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
            }
        }
    }, [editor, node, getPos]);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(source);
        } catch { /* ignore */ }
    }, [source]);

    const handlePaste = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                setEditValue(text);
                setEditing(true);
            }
        } catch { /* ignore */ }
    }, []);

    const handleCaptionBlur = useCallback(() => {
        setEditingCaption(false);
    }, []);

    const handleCaptionKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === "Escape") {
            setEditingCaption(false);
        }
    }, []);

    return (
        <NodeViewWrapper className="tikz-block-wrapper" data-type="tikz-block">
            {editing ? (
                <div className="tikz-editor">
                    <div className="tikz-editor__header">
                        <span className="tikz-editor__label">TikZ Source</span>
                        <div className="tikz-editor__actions">
                            <button
                                className="tikz-editor__btn tikz-editor__btn--cancel"
                                onClick={() => { setEditValue(source); setEditing(false); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="tikz-editor__btn tikz-editor__btn--save"
                                onClick={handleSave}
                            >
                                Render ⌘↵
                            </button>
                        </div>
                    </div>
                    <textarea
                        ref={textareaRef}
                        className="tikz-editor__textarea"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        spellCheck={false}
                        rows={Math.max(6, editValue.split("\n").length + 1)}
                    />
                </div>
            ) : (
                <div className="tikz-preview" onDoubleClick={() => { setEditValue(source); setEditing(true); }}>
                    {renderState === "loading" && (
                        <div className="tikz-preview__loading">
                            <div className="tikz-preview__spinner" />
                            <span>Compiling TikZ… (first render may take a few seconds)</span>
                        </div>
                    )}
                    {renderState === "error" && (
                        <div className="tikz-preview__error">
                            <span>TikZ render failed: {errorMsg}</span>
                            <button className="tikz-editor__btn" onClick={() => { setEditValue(source); setEditing(true); }}>
                                Edit Source
                            </button>
                        </div>
                    )}
                    {renderState === "done" && svgHtml && (
                        <div
                            className="tikz-preview__svg"
                            dangerouslySetInnerHTML={{ __html: svgHtml }}
                        />
                    )}
                    <div className="tikz-preview__overlay">
                        <button className="tikz-preview__btn" onClick={() => { setEditValue(source); setEditing(true); }}>
                            Edit
                        </button>
                        <button className="tikz-preview__btn" onClick={handleCopy}>
                            Copy
                        </button>
                        <button className="tikz-preview__btn" onClick={handlePaste}>
                            Paste
                        </button>
                        <button className="tikz-preview__btn tikz-preview__btn--danger" onClick={handleDelete}>
                            Delete
                        </button>
                    </div>
                </div>
            )}
            <figcaption className="tikz-caption">
                {editingCaption ? (
                    <input
                        ref={captionRef}
                        type="text"
                        className="tikz-caption__input"
                        value={caption}
                        onChange={(e) => updateAttributes({ caption: e.target.value })}
                        onBlur={handleCaptionBlur}
                        onKeyDown={handleCaptionKeyDown}
                        autoFocus
                        placeholder="Enter caption…"
                    />
                ) : (
                    <span
                        className="tikz-caption__text"
                        onClick={() => setEditingCaption(true)}
                        title="Click to edit caption"
                    >
                        {caption || "Click to add caption"}
                    </span>
                )}
            </figcaption>
        </NodeViewWrapper>
    );
}

// ── TipTap Node definition ───────────────────────────────────────────────────

export const TikzBlock = Node.create({
    name: "tikzBlock",
    group: "block",
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            source: { default: "" },
            caption: { default: "" },
            label: { default: "" },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="tikz-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "tikz-block" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TikzNodeView, {
            stopEvent: ({ event }) => {
                if (event.type === "mousedown" || event.type === "dblclick") return true;
                const target = event.target as HTMLElement;
                if (target.closest("textarea") || target.closest("input")) return true;
                if (event.type === "paste" || event.type === "copy" || event.type === "cut" || event.type === "input" || event.type === "drop") return true;
                return false;
            },
        });
    },
});
